import { defineStore } from 'pinia';
import useEmploymentStore, {
  StatePayDetail,
} from '@/modules/people/store/employment';
import { getIsDeaEnabled } from './dea-utils';
import { DateTime } from 'luxon';
import usePeopleStore from '@/modules/people/store';
import Employee from '@/modules/people/types/employee';
import cloneDeep from 'lodash/cloneDeep';
import { remove } from '@/lib/request/webapp/apiv2';
import { APIResponseSuccess } from '@/lib/types/request/types';


// eslint-disable-next-line prettier/prettier
export type DateString = `${number}${number}${number}${number}-${number}${number}-${number}${number}`;

export function getDateStringFromIso(date: string | undefined): DateString | undefined {
  return date ? DateTime.fromISO(date, { setZone: true }).toFormat('yyyy-MM-dd') as DateString : undefined;
}

export interface EffectiveDatePayDetails {
  effectiveDate: DateString;
  effectiveDateLocal: DateString;
  memorizedPayDetails: StatePayDetail[] | undefined;
  memorizedPeopleEmployee: Partial<Employee> | undefined;
}

interface DeaStore {
  createPayDetails: {
    isOpenCreatePayDetailsModal: boolean;
    newEffectiveDateLocal: DateString | undefined;
    creatingNewPayDetails: boolean;
  };
  effectiveDatePayDetails: Record<DateString, EffectiveDatePayDetails>;
  effectiveDatesList: Array<DateString>;
  selectingEffectiveDate: DateString | undefined;
  currentEffectiveDate: DateString | undefined;
  loadingNewDateEffectivePayDetails: boolean;
}

const useDeaStore = defineStore('webapp:people:date-effective-agreements', {
  state: (): DeaStore => {
    return {
      createPayDetails: {
        isOpenCreatePayDetailsModal: false,
        newEffectiveDateLocal: undefined,
        creatingNewPayDetails: false,
      },
      effectiveDatePayDetails: {},
      selectingEffectiveDate: undefined,
      effectiveDatesList: [],
      currentEffectiveDate: undefined,
      loadingNewDateEffectivePayDetails: false,
    };
  },
  getters: {
    selectingEffectivePayDetails: (state: DeaStore): EffectiveDatePayDetails | undefined => {
      return state.selectingEffectiveDate ? state.effectiveDatePayDetails[state.selectingEffectiveDate] : undefined;
    },
  },
  actions: {
    memorizedPayDetailsAndEmployee(): void {
      // Todo: remove circular dependency in useEmploymentStore and we don't need to repeadly run useEmploymentStore to get the local pay details
      const employmentState = useEmploymentStore();
      const peopleState = usePeopleStore();
      if(this.selectingEffectivePayDetails) {
        this.selectingEffectivePayDetails.memorizedPayDetails = cloneDeep(employmentState.getLocalPayDetails());
        this.selectingEffectivePayDetails.memorizedPeopleEmployee = cloneDeep(peopleState.employee);
      }
    },
    restorePayDetailsAndEmployee(): void {
      // Todo: remove circular dependency in useEmploymentStore and we don't need to repeadly run useEmploymentStore to get the local pay details
      const employmentState = useEmploymentStore();
      const peopleState = usePeopleStore();
      if(this.selectingEffectivePayDetails) {
        employmentState.setLocalPayDetails(this.selectingEffectivePayDetails.memorizedPayDetails ?? []);
        peopleState.setEmployee(this.selectingEffectivePayDetails.memorizedPeopleEmployee ?? {});
      }
    },
    openCreatePayDetailsModal(): void {
      // Before opening add new detail, we need to memorize the pay details
      this.memorizedPayDetailsAndEmployee();
      const employmentState = useEmploymentStore();
      const clonedPayDetailsActive = employmentState.getLocalPayDetails().filter(payDetails => payDetails.active);
      employmentState.setLocalPayDetails(clonedPayDetailsActive);
      this.createPayDetails.newEffectiveDateLocal = DateTime.utc().toFormat('yyyy-MM-dd') as DateString;
      this.createPayDetails.isOpenCreatePayDetailsModal = true;
    },

    closeCreatePayDetailsModal(): void {
      this.createPayDetails.isOpenCreatePayDetailsModal = false;
      this.restorePayDetailsAndEmployee();
    },

    openAddNewDetail(): void {
      const employmentState = useEmploymentStore();
      employmentState.addPayDetailByIndex();
    },
    getCurrentEffectiveDate(): DateString | undefined {
      return this.currentEffectiveDate;
    },

    initializeDateEffectivePayDetails(): void {
      const isDeaEnabled = getIsDeaEnabled();
      if (isDeaEnabled) {
        const peopleState = usePeopleStore();
        const effectiveDate = getDateStringFromIso(peopleState.getEmployeeAgreementsByIndex(0)?.EffectiveDate);
        const hireOn = getDateStringFromIso(peopleState.employee?.StartDate);
        const dateEffectiveToUse = effectiveDate || hireOn;

        if (dateEffectiveToUse) {
          const edPayDetails: EffectiveDatePayDetails = {
            effectiveDate: dateEffectiveToUse,
            effectiveDateLocal: dateEffectiveToUse,
            memorizedPayDetails: undefined,
            memorizedPeopleEmployee: undefined,
          };
          this.effectiveDatePayDetails[edPayDetails.effectiveDate] = edPayDetails;
          this.selectingEffectiveDate = edPayDetails.effectiveDate;
        }
        this.currentEffectiveDate = getDateStringFromIso(peopleState.employee?.AgreementCurrentEffectiveDate) as DateString;
        this.effectiveDatesList = (peopleState.employee?.AgreementEffectiveDateArray as Array<DateString> || [])
          .sort((a, b) => DateTime.fromISO(a).toMillis() - DateTime.fromISO(b).toMillis());
      }
    },

    setLoadingNewDateEffectivePayDetails(loading: boolean): void {
      this.loadingNewDateEffectivePayDetails = loading;
    },

    setCreatingNewPayDetails(creatingNewPayDetails: boolean): void {
      this.createPayDetails.creatingNewPayDetails = creatingNewPayDetails;
    },

    changeSelectingEffectiveDate(effectiveDate: DateString): void {
      this.selectingEffectiveDate = effectiveDate;
    },

    getAllEffectiveDatesAvailable(): DateString[] {
      return this.effectiveDatesList;
    },

    async deleteUpcomingPayDetail(
      agreementIds: number[]
    ): Promise<boolean> {
      try {
        const results = await Promise.all(agreementIds.map(id => remove<APIResponseSuccess>(`api/management/v2/employee-agreements/${id}`)));
        const success = results.every(result => result.data.success);
        if (!success) {
          throw new Error('Failed to delete upcoming pay details');
        }
        return success;
      } catch (error) {
        throw error;
      }
    },

    validateLocalDateEffective(): string | undefined {
      const isToCreate = this.createPayDetails.isOpenCreatePayDetailsModal;
      const isDateExists = (date: DateString) =>
        this.getAllEffectiveDatesAvailable().some(effectiveDateAvailable => {
          return (
            effectiveDateAvailable === date
          );
        });
      if (isToCreate) {
        const newEffectiveDate = this.createPayDetails.newEffectiveDateLocal;
        return newEffectiveDate && isDateExists(newEffectiveDate)
          ? 'This effective date is already in use'
          : undefined;
      }
      if (!this.selectingEffectivePayDetails) {
        return undefined;
      }

      const {
        effectiveDate,
        effectiveDateLocal,
      } = this.selectingEffectivePayDetails;

      if (effectiveDate === effectiveDateLocal) {
        return undefined;
      }

      return isDateExists(effectiveDateLocal)
        ? 'This date is already used'
        : undefined;
    },
  },
});
export default useDeaStore;
